import '../styles/globals.css'
import React from 'react'
import Head from 'next/head'
import type { AppProps } from 'next/app'
import { builder, Builder } from '@builder.io/react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'
import builderConfig from '../config/builder'
import '@fortawesome/fontawesome-svg-core/styles.css' // import Font Awesome CSS
import { config } from '@fortawesome/fontawesome-svg-core'
config.autoAddCss = false // Tell Font Awesome to skip adding the CSS automatically since it's being imported above
import '@fontsource/shadows-into-light/400.css'
import '../styles/fonts.css'
import Script from 'next/script'
import { Router } from 'next/router'
import { useEffect } from 'react'
import * as gtag from '../src/common/utilities/gtag'
import { globalCss } from '../src/stitches.config'
import GlobalLayout from '../src/common/components/elements/Layout/GlobalLayout'
import PhoneNumberReplacer from '../src/common/components/marketing/text/PhoneNumbers/PhoneNumberReplacer'

// Initialize builder with your apiKey
builder.init(builderConfig.apiKey)
Builder.isStatic = true
// From Builder example - Heavy handed disable fonts for perf
builder.allowCustomFonts = false

const globalStyles = globalCss({
    body: { color: '$dark' },
    'h1, h2, h3, h4, h5, h6, h7': { color: '$darkest' },
    h1: {
        fontSize: '$8',
        '@md': {
            fontSize: '$9',
        },
    },
    h2: {
        fontSize: '$7',
        '@md': {
            fontSize: '$8',
        },
    },
    h3: {
        fontSize: '$6',
        '@md': {
            fontSize: '$7',
        },
    },
    h5: {
        fontSize: '$5',
        '@md': {
            fontSize: '$8',
        },
    },
    h6: {
        fontSize: '$4',
        '@md': {
            fontSize: '$5',
        },
    },
    h7: {
        fontSize: '$3',
        '@md': {
            fontSize: '$6',
        },
    },
    p: {
        color: '$dark',
    },
    '.builder-text a': {
        textDecoration: 'underline',
        color: '$primary',
        '&:hover': {
            textDecoration: 'none',
        },
    },
    '.builder-text ul': {
        marginBottom: '1rem',
    },
    '.pgc-table': {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: '0 15px',
        '& thead': {
            textAlign: 'left',
        },
    },
})

function isCorporatePath(router: Router): boolean {
    return Boolean(
        !router.isFallback && router.asPath.split('/')[1] === 'corporate'
        // And/Or we could use: Array(pageProps.content?.data?.pagetypeTag).includes('corporate').
        // And/Or we could use: pageProps.content?.data.themeId) === 'whatever is set within builder...'
    )
}

export default function MyApp(props: AppProps) {
    globalStyles()
    const { Component, pageProps, router } = props
    const pageTitle =
        (pageProps?.content?.data?.title ?? '') + ' | PerfectGift.com'
    const pageDescription = pageProps?.content?.data?.description ?? ''
    const canonicalRef = pageProps?.content?.data?.canonicalRef
    const [queryClient] = React.useState(() => new QueryClient())
    useEffect(() => {
        const handleRouteChange = (url: string) => {
            gtag.pageview(url)
        }
        router.events.on('routeChangeComplete', handleRouteChange)
        router.events.on('hashChangeComplete', handleRouteChange)
        return () => {
            router.events.off('routeChangeComplete', handleRouteChange)
            router.events.off('hashChangeComplete', handleRouteChange)
        }
    }, [router.events])
    const urlPath = router.asPath.split('?')[0]

    return (
        <>
            <Head>
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1"
                />
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link
                    rel="canonical"
                    href={`https://www.perfectgift.com${
                        canonicalRef ?? urlPath
                    }`}
                />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:locale" content="en_US" />
                <meta property="og:type" content="website" />
                <meta
                    property="og:image"
                    content="/images/perfect_gift_logo_og.png"
                />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="627" />
                {/*PG does not currently have twitter*/}
                {/*<meta name="twitter:title" content={pageTitle} />*/}
                {/*<meta name="twitter:description" content={pageDescription} />*/}
                {/*<meta name="twitter:card" content="summary" />*/}
                {/*<meta name="twitter:site" content="@JoinGiftYa" />*/}
            </Head>
            {/* Global Site Tag (gtag.js) - Google Analytics */}
            <Script
                strategy="worker"
                src={`https://www.googletagmanager.com/gtag/js?id=${gtag.GA_TRACKING_ID}`}
            />
            <Script
                id="gtag-init"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: `
                        window.dataLayer = window.dataLayer || [];
                        function gtag(){dataLayer.push(arguments);}
                        gtag('js', new Date());
                        gtag('config', '${gtag.GA_TRACKING_ID}', {
                          page_path: window.location.pathname,
                        });
                      `,
                }}
            />
            <Script
                id="gtm-init"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: `
                    function font_faml_uyoqvna(w, d, s, l, i) {
                        w[l] = w[l] || [];
                        w[l].push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
                        var f = d.getElementsByTagName(s)[0], j = d.createElement(s),
                        dl = l != 'dataLayer' ? '&l=' + l : '';
                        // j.async = true;
                        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
                        f.parentNode.insertBefore(j, f);
                    }
                    font_faml_uyoqvna(window, document, 'script', 'dataLayer', "${process.env.NEXT_PUBLIC_GTM_ID}");
            `,
                }}
            />
            <QueryClientProvider client={queryClient}>
                <GlobalLayout
                    Component={Component}
                    pageProps={pageProps}
                    isCorporate={isCorporatePath(router)}
                />
                <PhoneNumberReplacer isCorporate={isCorporatePath(router)} />
                <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
        </>
    )
}
